/**
 * Explorador de permisos: la pantalla central de la consola.
 *
 * Tres cosas en una sola vista, y a propósito en una sola:
 *
 * 1. La lista de roles con cuánta gente los tiene.
 * 2. La ficha del rol elegido (qué puede, qué no, qué registros ve).
 * 3. El buscador por capacidad — se escribe «facturar» y la lista se reduce a
 *    los roles que lo permiten, mostrando POR QUÉ cada uno aparece.
 *
 * El buscador no es una pantalla aparte porque la pregunta que responde
 * («¿cuál le doy?») se termina de contestar leyendo la ficha: separarlos
 * obligaría a ir y volver. El comparador sí toma el panel derecho entero, porque
 * ahí la pregunta ya es otra.
 */

import { useEffect, useMemo, useState } from 'react';

import { Badge, Button } from '@/components/shell/primitives';
import { RUTA_ROLES } from '@/panel/api';
import { buscarPorCapacidad } from '@/panel/buscar';
import { ComparadorRoles } from '@/panel/ComparadorRoles';
import type { Role } from '@/panel/contract';
import { etiquetasDeArea, universoDeModelos } from '@/panel/contract';
import { describirError } from '@/panel/errores';
import type { UniversoModelos } from '@/panel/FichaRol';
import { FichaRol } from '@/panel/FichaRol';
import { useRoles } from '@/panel/queries';
import { CampoBusqueda, Cargando, Fallo, FilaMaestra, Panel, Vacio, useNavegacionPorFlechas } from '@/panel/ui';

function ContadorDeGente({ rol }: { rol: Role }) {
  if (rol.userCount === null) return <span className="text-xs text-fg-faint">sin dato</span>;
  return (
    <span className="text-xs text-fg-muted tabular-nums">
      {rol.userCount} {rol.userCount === 1 ? 'persona' : 'personas'}
    </span>
  );
}

export function PermisosScreen() {
  const { data: roles, isPending, error, refetch } = useRoles();

  const [consulta, setConsulta] = useState('');
  const [codigoElegido, setCodigoElegido] = useState<string | null>(null);
  const [codigoComparado, setCodigoComparado] = useState<string | null>(null);

  const universo: UniversoModelos = useMemo(
    () => ({
      modelos: universoDeModelos(roles ?? []),
      areas: etiquetasDeArea(roles ?? []),
    }),
    [roles],
  );

  const coincidencias = useMemo(() => buscarPorCapacidad(roles ?? [], consulta), [roles, consulta]);
  const visibles = useMemo(
    () => (coincidencias === null ? (roles ?? []) : coincidencias.map((c) => c.role)),
    [coincidencias, roles],
  );

  // La selección sigue a lo que se está viendo: si el filtro dejó afuera al rol
  // abierto, el panel derecho quedaría hablando de algo que ya no está en la
  // lista. Se pasa al primero de los visibles.
  useEffect(() => {
    if (visibles.length === 0) return;
    if (codigoElegido !== null && visibles.some((rol) => rol.code === codigoElegido)) return;
    setCodigoElegido(visibles[0]?.code ?? null);
    setCodigoComparado(null);
  }, [visibles, codigoElegido]);

  const elegido = (roles ?? []).find((rol) => rol.code === codigoElegido) ?? null;
  const comparado = (roles ?? []).find((rol) => rol.code === codigoComparado) ?? null;
  const moverFoco = useNavegacionPorFlechas();

  if (isPending) {
    return (
      <Panel title="Permisos y roles">
        <Cargando label="Consultando los roles del sistema…" />
      </Panel>
    );
  }

  if (error !== null) {
    const fallo = describirError(error, RUTA_ROLES);
    return (
      <div className="space-y-4">
        <Fallo
          title={fallo.title}
          message={fallo.message}
          detail={fallo.detail}
          onRetry={() => {
            void refetch();
          }}
        />
        {fallo.faltaEndpoint && (
          <Panel title="Mientras tanto">
            <p className="text-sm text-fg-muted">
              La consola no inventa permisos: prefiere no mostrar nada antes que mostrar una foto que no
              es la de este sistema. La asignación de roles en la pestaña «Usuarios» también espera este
              servicio, porque sin él no sabe qué otorga cada rol.
            </p>
          </Panel>
        )}
      </div>
    );
  }

  if (roles === undefined || roles.length === 0) {
    return (
      <Panel title="Permisos y roles">
        <Vacio
          title="No hay roles definidos"
          description="El servidor respondió correctamente pero no devolvió ningún rol. Los roles los declaran los módulos en su archivo de seguridad."
        />
      </Panel>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[22rem_minmax(0,1fr)] xl:grid-cols-[24rem_minmax(0,1fr)]">
      {/* ---------------------------------------------------------------- */}
      {/* Columna izquierda: buscar y elegir                                */}
      {/* ---------------------------------------------------------------- */}
      <div className="lg:sticky lg:top-[6.5rem] lg:self-start">
        <Panel bodyClassName="p-3" className="mb-3">
          <CampoBusqueda
            id="buscar-capacidad"
            label="¿Qué necesita poder hacer?"
            placeholder="facturar, cobrar, ver stock…"
            value={consulta}
            onChange={setConsulta}
          />
          <p className="mt-2 text-xs text-fg-muted">
            {coincidencias === null
              ? `${roles.length} ${roles.length === 1 ? 'rol' : 'roles'} en el sistema.`
              : coincidencias.length === 0
                ? 'Ningún rol permite eso en este sistema.'
                : `${coincidencias.length} ${coincidencias.length === 1 ? 'rol lo permite' : 'roles lo permiten'}.`}
          </p>
        </Panel>

        <Panel title="Roles" bodyClassName="p-0">
          {visibles.length === 0 ? (
            <div className="p-3">
              <Vacio
                title="Sin coincidencias"
                description="Probá con otra palabra: el buscador mira lo que cada rol permite hacer, no su nombre."
              />
            </div>
          ) : (
            <ul className="max-h-[60vh] overflow-y-auto" onKeyDown={moverFoco}>
              {visibles.map((rol) => {
                const coincidencia = coincidencias?.find((c) => c.role.code === rol.code);
                return (
                  <FilaMaestra
                    key={rol.code}
                    selected={rol.code === codigoElegido}
                    onSelect={() => {
                      setCodigoElegido(rol.code);
                      setCodigoComparado(null);
                    }}
                  >
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-sm font-medium">{rol.name}</span>
                      <ContadorDeGente rol={rol} />
                    </span>
                    <span className="mt-0.5 block truncate font-mono text-2xs text-fg-faint">{rol.code}</span>
                    {coincidencia !== undefined && coincidencia.motivos.length > 0 && (
                      <span className="mt-1.5 block space-y-0.5">
                        {coincidencia.motivos.map((motivo) => (
                          <span key={motivo} className="block truncate text-xs text-fg-muted">
                            · {motivo}
                          </span>
                        ))}
                      </span>
                    )}
                  </FilaMaestra>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Columna derecha: ficha o comparación                              */}
      {/* ---------------------------------------------------------------- */}
      {/* `min-w-0`: un hijo de grilla no baja de su ancho de contenido salvo
          que se lo pidan, y la tabla del comparador (min-w-[36rem]) estiraba
          la pagina entera en movil en vez de scrollear dentro de su caja. */}
      <div className="min-w-0">
        {elegido === null ? (
          <Panel>
            <Vacio title="Elegí un rol de la lista" />
          </Panel>
        ) : comparado !== null ? (
          <ComparadorRoles
            a={elegido}
            b={comparado}
            universo={universo}
            onCerrar={() => {
              setCodigoComparado(null);
            }}
          />
        ) : (
          <div className="space-y-4">
            <Panel bodyClassName="p-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[14rem] flex-1">
                  <label
                    htmlFor="comparar-con"
                    className="mb-1 block text-2xs font-semibold uppercase tracking-[0.08em] text-fg-faint"
                  >
                    Comparar «{elegido.name}» con otro rol
                  </label>
                  <select
                    id="comparar-con"
                    value=""
                    onChange={(evento) => {
                      setCodigoComparado(evento.target.value === '' ? null : evento.target.value);
                    }}
                    className="h-9 w-full rounded-md border border-line bg-surface px-2 text-sm hover:border-line-strong"
                  >
                    <option value="">Elegí un rol para ver la diferencia…</option>
                    {/* El nombre solo NO alcanza: hay dos roles «Contable» y dos
                        «Vendedor» —los declaran módulos distintos— y elegir
                        entre dos renglones idénticos es tirar una moneda. Un
                        `<option>` no admite marcado, así que el código técnico
                        va en el mismo texto: es el único identificador único que
                        el usuario puede cotejar contra la ficha. */}
                    {roles
                      .filter((rol) => rol.code !== elegido.code)
                      .map((rol) => (
                        <option key={rol.code} value={rol.code}>
                          {rol.name} — {rol.code}
                        </option>
                      ))}
                  </select>
                </div>
                {elegido.module !== null && (
                  <p className="pb-1.5">
                    <Badge tone="neutral">Lo declara el módulo {elegido.module}</Badge>
                  </p>
                )}
                {consulta.trim() !== '' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setConsulta('');
                    }}
                  >
                    Limpiar búsqueda
                  </Button>
                )}
              </div>
            </Panel>

            <FichaRol rol={elegido} universo={universo} />
          </div>
        )}
      </div>
    </div>
  );
}
